/* import __COLOCATED_TEMPLATE__ from './trial-info-box.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import type FinTrialService from 'embercom/services/fin-trial-service';
import { AiAnswersState, selectedAgentToTrialType } from 'embercom/lib/operator/fin/types';
import { AGENT_OPTIONS } from 'embercom/lib/operator/resolution-bot/constants';
import moment from 'moment-timezone';

interface Args {
  selectedAgentValue: string;
}

interface Signature {
  Args: Args;
}

export default class TrialInfoBox extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare finTrialService: FinTrialService;

  @tracked showTrialModal = false;

  private get agentTrialType() {
    return selectedAgentToTrialType(this.args.selectedAgentValue);
  }

  get showTrialBox(): boolean {
    return (
      this.appService.app.canUseFeature('fin-agentic-trials') &&
      this.finTrialService.showAgentCTAs(this.agentTrialType)
    );
  }

  get agentDisplayName(): string {
    let agentValue = this.args.selectedAgentValue;
    if (agentValue === AGENT_OPTIONS.FIN_SDR) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.sales-agent');
    } else if (agentValue === AGENT_OPTIONS.FIN_ECOMMERCE) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.ecommerce-agent');
    } else if (agentValue === AGENT_OPTIONS.FIN_SUCCESS) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.customer-success-agent');
    }
    return this.intl.t('operator.workflows.visual-builder.fin.agents.service-agent');
  }

  get trialBoxTranslationKey(): string {
    let agentState = this.finTrialService.getAgentTrialState(this.agentTrialType);
    if (agentState?.state === AiAnswersState.trialStarted && agentState.trialDetails?.endDate) {
      return 'operator.workflows.visual-builder.pass-to-fin.settings.trial-box.trial-ends';
    }
    return 'operator.workflows.visual-builder.pass-to-fin.settings.trial-box.start-trial';
  }

  get trialRemainingDays(): number {
    let agentState = this.finTrialService.getAgentTrialState(this.agentTrialType);
    if (agentState?.trialDetails?.endDate) {
      return moment(agentState.trialDetails.endDate).diff(moment(), 'days');
    }
    return 0;
  }

  @action
  openTrialModal() {
    this.showTrialModal = true;
  }

  @action
  closeTrialModal() {
    this.showTrialModal = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::TrialInfoBox': typeof TrialInfoBox;
  }
}
